// Status de proposta: config do filtro/chip + validade legível. `expirada` é
// derivada no backend (campo booleano) — aqui só é traduzida em status visual.
// Mesmo vocabulário do sistema E: ponto + frase (docs/DESIGN.md).
import { DOT, StatusDot } from '@/components/Page';
import { cn } from '@/lib/utils';
import { etapaFinanciamento } from '@/lib/pagamento';

const cfg = (label, tone) => ({ label, tone, color: DOT[tone] });

// Etapa do financiamento. O tom diz quem age: a fila espera o time de Financiamentos (âmbar), a análise anda na
// financeira (cobalto), aprovado conclui e negado volta ao vendedor (vermelho).
const ETAPA_FINANCIAMENTO = {
  aguardando: cfg('Aguardando análise', 'att'),
  analise: cfg('Em análise', 'run'),
  aprovado: cfg('Aprovado', 'ok'),
  negado: cfg('Negado', 'bad'),
};

// A etapa que a tela mostra: a do financiamento, menos na proposta recusada (saiu da fila).
export const etapaVisivel = (proposta) => (proposta?.status === 'recusada' ? null : etapaFinanciamento(proposta));

// Chip da etapa ("Em análise na BV Financeira"; no negado, o motivo ao lado, cortado), na lista e no documento,
// para todos. `comMotivo={false}`: quem chama mostra o motivo inteiro.
export const EtapaFinanciamentoChip = ({ proposta, className = '', comMotivo = true }) => {
  const etapa = etapaVisivel(proposta);
  if (!etapa) return null;
  const { label, tone } = ETAPA_FINANCIAMENTO[etapa];
  const { financeira, motivo } = proposta.financiamento || {};
  return (
    <span className={cn('flex min-w-0 items-center gap-2 text-[13.5px]', className)} data-testid={`etapa-financiamento-${proposta.numero_proposta}`}>
      <StatusDot tone={tone} className="shrink-0 text-[length:inherit]">
        {etapa === 'analise' && financeira ? `${label} na ${financeira}` : label}
      </StatusDot>
      {comMotivo && etapa === 'negado' && motivo && <span className="truncate text-gray-500" title={motivo}>{motivo}</span>}
    </span>
  );
};

export const PROPOSTA_STATUS_CONFIG = {
  rascunho: cfg('Rascunho', 'off'),
  enviada: cfg('Enviada', 'run'),
  expirada: cfg('Expirada', 'att'),
  convertida: cfg('Convertida em pedido', 'ok'),
  recusada: cfg('Recusada', 'bad'),
};

export const statusEfetivo = (p) => (p?.expirada ? 'expirada' : p?.status);

export const PropostaChip = ({ proposta, className = '' }) => {
  const c = PROPOSTA_STATUS_CONFIG[statusEfetivo(proposta)] || PROPOSTA_STATUS_CONFIG.rascunho;
  return <StatusDot tone={c.tone} className={className}>{c.label}</StatusDot>;
};

// "expira em 3 dias" / "expira hoje" / "expirada há 2 dias"; vazio nos terminais.
export const textoValidade = (p) => {
  if (!p?.validade_ate || ['convertida', 'recusada'].includes(p.status)) return '';
  const [y, m, d] = String(p.validade_ate).slice(0, 10).split('-').map(Number);
  const validade = new Date(y, m - 1, d);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const dias = Math.round((validade - hoje) / 86400000);
  if (dias > 1) return `expira em ${dias} dias`;
  if (dias === 1) return 'expira amanhã';
  if (dias === 0) return 'expira hoje';
  return dias === -1 ? 'expirada há 1 dia' : `expirada há ${-dias} dias`;
};
